import type { CurrentSession } from '@nlc/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  resyncUploads,
  retryPendingUploads,
  subscribeUploads,
  uploadQueueFor,
  uploadScopeOf,
} from '../../api/uploads.js';

/**
 * Retries queued uploads once after sign-in (this mounts with the shell) and whenever the
 * browser reports the connection is back. Coming back to the tab re-reads the store so uploads
 * another tab finished or discarded disappear here too. A finished upload refreshes every
 * document list so the file shows up wherever it was meant to go, even if that screen is no
 * longer open.
 */
export function useUploadRetry(session: CurrentSession): void {
  const queryClient = useQueryClient();
  const { activeOrganizationId, membershipId } = session;

  useEffect(() => {
    const queue = uploadQueueFor(uploadScopeOf({ activeOrganizationId, membershipId }));
    const retry = (): void => {
      void retryPendingUploads(queue);
    };
    const resync = (): void => {
      if (document.visibilityState === 'visible') void resyncUploads(queue);
    };
    retry();
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', resync);
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', resync);
    };
  }, [activeOrganizationId, membershipId]);

  useEffect(
    () =>
      subscribeUploads((event) => {
        if (event.type !== 'completed') return;
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes('documents'),
        });
      }),
    [queryClient],
  );
}
